import { Sentry } from '@shared/sentry';
import { env } from '@shared/config';

declare global {
  interface Window {
    ym(
      metricId: string,
      method: 'reachGoal',
      goal: string,
      params?: Record<string, unknown>,
    ): void;
  }
}

/**
 * @description Names of all dev metrics that we collect
 *
 * It should start with "dev." to be collected correctly
 */
type DevMetricMetrics =
  | 'dev.search.empty_results'
  | 'dev.search.time_to_display_offers'
  | 'dev.search.max_poll_attempts'
  | 'dev.hot_tours.empty_results'
  | 'dev.hot_tours.time_to_display_offers'
  | 'dev.hot_tours.redirect_desktop'
  | 'dev.hot_tours.redirect_mobile'
  | 'dev.hot_tours_by_id.redirect_desktop'
  | 'dev.hot_tours_by_id.redirect_mobile'
  | 'dev.second_search.empty_results'
  | 'dev.second_search.time_to_display_offers'
  | 'dev.second_search.max_poll_attempts'
  | 'dev.sw.unregister'
  | 'dev.tourder.time_to_init'
  | 'dev.tourder.reset_consumer_token'
  | 'dev.tourx.old_url_redirect_count'
  | 'dev.tourbnb.time_to_load_ab_test'
  | 'dev.tourbnb.redirect_to_ab_test'
  | 'dev.hot_tours.redirect_mobile'
  | 'dev.legacy_last_search_cleared';

export class DevMetrics {
  private metricId: string;

  constructor(metricId: string) {
    this.metricId = metricId;

    if (!metricId) {
      Sentry.captureExWithCause('ERR_METRICS_INVALID_METRIC_ID', {
        captureContext: { extra: { metricId } },
      });
    }
  }

  logMetric(metricName: DevMetricMetrics, params?: Record<string, unknown>) {
    try {
      /**
       * @note Yandex metrika displays params in UI.
       * Without params nothing will be displayed in UI, so
       * we need to duplicate metricName to params for them to appear
       *
       * For more details see the article with explanation about Yandex metrika
       * goals and params
       *
       * @see https://osipenkov.ru/yandex-metrika-events-parameters/
       */
      const finalParams = {
        [metricName]: params || null, // event with undefined params is skipped, so we need to send null in this case
      };

      window.ym(this.metricId, 'reachGoal', metricName, finalParams);
    } catch (cause) {
      Sentry.captureExWithCause('ERR_METRICS_FAILED_TO_SEND', {
        cause,
        captureContext: { extra: { metricName, params } },
      });
    }
  }

  increment(metricName: DevMetricMetrics) {
    this.logMetric(metricName, { count: 1 });
  }

  timing(metricName: DevMetricMetrics, timing: number) {
    this.logMetric(metricName, { timing });
  }
}

export const devMetrics = new DevMetrics(env.YA_METRIKA_ID ?? '');
